import en from '@/lang/en_EN.json';
import es from '@/lang/es_LA.json';
import pt from '@/lang/pt_BR.json';
import it from '@/lang/it_IT.json';

const langMap = {
  es: 'guarderia-equipaje',
  en: 'luggage-storage',
  pt: 'guarda-volumes',
  it: 'deposito-bagagli',
};
const ItemListNames = {
  es: 'Ciudades donde WeKeep ofrece guardado seguro de equipaje',
  en: 'Cities where WeKeep offers safe luggage storage',
  pt: 'Cidades onde a WeKeep oferece guarda-volumes seguro',
  it: 'Città dove WeKeep offre deposito bagagli sicuro',
};

const baseUrl = process.env.VUE_APP_URL;

function stripHtml(html) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
}

function getTextsFromTranslationFile(subArray, lang) {
  let file = es;
  if (lang === 'en') file = en;
  if (lang === 'pt') file = pt;
  if (lang === 'it') file = it;
  return file[subArray];
}

function generateSchema(lang, name, path, title, description) {
  // 1 - Schema: Organization
  if (!document.querySelector('script[type="application/ld+json"][data-schema="organization"]')) {
    const orgSchema = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'WeKeep',
      legalName: 'WeKeep Travel Services LLC',
      alternateName: title,
      url: 'https://wekeep.app',
      logo: 'https://wekeep.app/img/logo_wekeep.png',
      description,
      foundingDate: '2021',
      founders: [
        {
          '@type': 'Person',
          name: 'Carlos',
        },
        {
          '@type': 'Person',
          name: 'Sofía',
        },
      ],
      address: {
        '@type': 'PostalAddress',
        streetAddress: '16192 Coastal Highway',
        addressLocality: 'Lewes',
        addressRegion: 'Delaware',
        postalCode: '19958',
        addressCountry: 'US',
      },
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'customer support',
        email: 'info@wekeep.app',
        telephone: '+1 786 796 1553',
        availableLanguage: ['Spanish', 'English', 'Portuguese', 'Italian'],
      },
      sameAs: [
        'https://www.instagram.com/wekeep.app',
        'https://www.facebook.com/app.wekeep',
        'https://www.linkedin.com/company/wekeep-app',
        'https://www.tiktok.com/@wekeep.app',
      ],
    };

    const scriptOrg = document.createElement('script');
    scriptOrg.type = 'application/ld+json';
    scriptOrg.setAttribute('data-schema', 'organization');
    scriptOrg.textContent = JSON.stringify(orgSchema);
    document.head.appendChild(scriptOrg);
  }

  // 2 - Schema: Website
  if (!document.querySelector('script[type="application/ld+json"][data-schema="website"]')) {
    const websiteSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      url: 'https://wekeep.app/',
      name: 'WeKeep',
      alternateName: title,
      publisher: {
        '@type': 'Organization',
        name: 'WeKeep Travel Services LLC',
        logo: {
          '@type': 'ImageObject',
          url: 'https://wekeep.app/img/logo_wekeep.png',
        },
      },
    };

    const scriptWebsite = document.createElement('script');
    scriptWebsite.type = 'application/ld+json';
    scriptWebsite.setAttribute('data-schema', 'website');
    scriptWebsite.textContent = JSON.stringify(websiteSchema);
    document.head.appendChild(scriptWebsite);
  }

  // 3 - Schema: Listado de Ciudades
  if (!document.querySelector('script[type="application/ld+json"][data-schema="cities-list"]')) {
    const cities = getTextsFromTranslationFile('cities', lang);
    let itemList = {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: `${ItemListNames[lang]}`,
      itemListElement: Object.keys(cities).map((slug, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: cities[slug],
        url: `${baseUrl}/${lang}/${langMap[lang]}/${slug}/`,
      })),
    };

    itemList = JSON.stringify(itemList);
    const scriptItemList = document.createElement('script');
    scriptItemList.type = 'application/ld+json';
    scriptItemList.setAttribute('data-schema', 'cities-list'); // identificador único
    scriptItemList.innerHTML = itemList;
    document.head.appendChild(scriptItemList);
  }

  // 4 - Schema: LocalBusiness (solo en CityHome y LockerHome)
  if (name.includes('LockerHome')) {
    const existingLocalSchema = document.querySelector('script[type="application/ld+json"][data-schema="localbusiness"]');
    if (existingLocalSchema) existingLocalSchema.remove();

    // todo LockerHome hacer para más de una ubicación.
    const localSchema = {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      name: `${title}`,
      id: `${baseUrl}${path}#localbusiness`,
      url: `${baseUrl}${path}`,
      image: 'https://wekeep.app/img/logo_wekeep.png',
      description,
      priceRange: '$$',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Aeropuerto Internacional de Cordoba Ing. Ambrosio Taravella',
        addressLocality: 'Cordoba',
        addressRegion: 'Cordoba',
        addressCountry: 'AR',
      },
      areaServed: {
        '@type': 'Airport',
        name: 'Aeropuerto Internacional de Cordoba Ing. Ambrosio Taravella',
        iataCode: 'COR',
      },
      telephone: '+1 786 796 1553',

      sameAs: [
        'https://www.instagram.com/wekeep.app',
        'https://www.facebook.com/app.wekeep',
        'https://www.linkedin.com/company/wekeep-app',
        'https://www.tiktok.com/@wekeep.app',
      ],
    };

    const scriptLocal = document.createElement('script');
    scriptLocal.type = 'application/ld+json';
    scriptLocal.setAttribute('data-schema', 'localbusiness');
    scriptLocal.textContent = JSON.stringify(localSchema);
    document.head.appendChild(scriptLocal);
  } else if (name.includes('CityHome')) {
    const parts = path.split('/').filter(Boolean);
    const citySlug = parts[2];

    const cityName = citySlug
      ? citySlug.charAt(0).toUpperCase() + citySlug.slice(1).replace(/-/g, ' ')
      : '';

    const existingLocalSchema = document.querySelector('script[type="application/ld+json"][data-schema="localbusiness"]');
    if (existingLocalSchema) existingLocalSchema.remove();

    const localSchema = {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      name: `${title}`,
      id: `${baseUrl}${path}#localbusiness`,
      url: `${baseUrl}${path}`,
      image: 'https://wekeep.app/img/logo_wekeep.png',
      description,
      priceRange: '$4 - $6 USD',
      address: {
        '@type': 'PostalAddress',
        addressLocality: cityName,
      },
      areaServed: {
        '@type': 'City',
        name: cityName,
      },
      telephone: '+1 786 796 1553',

      sameAs: [
        'https://www.instagram.com/wekeep.app',
        'https://www.facebook.com/app.wekeep',
        'https://www.linkedin.com/company/wekeep-app',
        'https://www.tiktok.com/@wekeep.app',
      ],
    };

    const scriptLocal = document.createElement('script');
    scriptLocal.type = 'application/ld+json';
    scriptLocal.setAttribute('data-schema', 'localbusiness');
    scriptLocal.textContent = JSON.stringify(localSchema);
    document.head.appendChild(scriptLocal);
  }

  // 5 - Schema: FAQS (solo en Help)
  if (name.includes('Help') && !document.querySelector('script[type="application/ld+json"][data-schema="faqpage"]')) {
    const faqTexts = getTextsFromTranslationFile('help', lang);
    const faqQuestions = faqTexts.faq;
    const mainEntity = Object.keys(faqQuestions).map((key) => ({
      '@type': 'Question',
      name: faqQuestions[key],
      acceptedAnswer: {
        '@type': 'Answer',
        text: stripHtml(faqTexts[`${key}_text`]) || '',
        ...(key === 'how' && {
          video: {
            '@type': 'VideoObject',
            name: faqQuestions[key],
            description: faqQuestions[key],
            thumbnailUrl: 'https://img.youtube.com/vi/8EKkqxfpqNU/hqdefault.jpg',
            uploadDate: '2023-07-18T14:09:03-07:00',
            contentUrl: 'https://www.youtube.com/watch?v=8EKkqxfpqNU',
            embedUrl: 'https://www.youtube.com/embed/8EKkqxfpqNU',
          },
        }),
      },

    }));

    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity,
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-schema', 'faqpage');
    script.text = JSON.stringify(faqSchema, null, 2);
    document.head.appendChild(script);
  }
}

export { generateSchema };
